import { AnchorResolvedSource } from "./service";

/**
 * The customer's own draft state for one anchor, before a final submit — pulled out as pure,
 * framework-free functions so the substitution DECISION is independently unit-testable rather
 * than only living inside `AnchorConfirmContent.tsx`'s own state updaters (this repo has no React
 * Testing Library).
 *
 * Two things can be true about an anchor at once, tracked together on purpose:
 *   - a committed PLAY (`resolution` — `AnchorConfirmContent.tsx`'s doneCount/order-check source)
 *   - a committed CLIP substitution that hasn't (yet, or ever) had a play picked against it
 *
 * Substituting a clip is a decision in its own right: stepping the reel to a different clip
 * commits it immediately (`AnchorStage.tsx`'s `onSubstitute`), and `applyClipSubstitution` below
 * always records it, in the one place (`AnchorConfirmContent.tsx`'s own draft map) that survives
 * navigating to another anchor and back — the badge/undo control this state drives is read back
 * from here on every render, never from local component state.
 */

/** Same shape `POST .../anchors` submits per anchor (`SubmitAnchorPayload` in `service.ts`) minus
 *  `anchorId`, which the caller already knows from the draft map's key. */
export interface AnchorResolution {
  uid: string;
  clipAssetId: string;
  source: AnchorResolvedSource;
}

export interface AnchorDraftState {
  /** The committed play, if any — `null` means the anchor isn't resolved yet. */
  resolution: AnchorResolution | null;
  /** Set when the clip has been explicitly substituted and no resolution has re-anchored it
   *  since (`commitPlay` below always clears this — once a play is committed, the resolution's
   *  own `clipAssetId` is the single source of truth for this anchor's clip). `null` when the
   *  anchor is on its own suggested clip. */
  substitutedClipAssetId: string | null;
}

export const EMPTY_ANCHOR_DRAFT: AnchorDraftState = {
  resolution: null,
  substitutedClipAssetId: null,
};

/**
 * Stepping the reel to a different clip (`AnchorStage.tsx`'s `onSubstitute`, fired the moment a
 * stepped-to clip is known — no separate confirm step). Always records the substitution. Keeps an
 * already-committed play when either:
 *   - the caller can affirmatively confirm it's still visible in `newClipAssetId` via
 *     `committedPlayVisibleInNewClip` — the live frontend currently never can: there's no
 *     per-play, per-clip visibility data on the wire (the backend's own `playVisible` gate,
 *     `filmsGround.ts`'s `POST /:jobId/anchors`, is a per-CLIP b-roll flag, not a per-play one),
 *     so every real call site (`AnchorConfirmContent.tsx`'s `handleSubstitute`) passes `false`; or
 *   - `newClipAssetId` is literally the clip the play is already committed against right now —
 *     no visibility data is needed to know a pairing that hasn't changed is still valid.
 * That second case only covers a single hop landing back where it started; it does NOT survive
 * an intermediate step to a different clip in between — that step already clears `resolution`
 * (the first bullet doesn't apply to it either), so there's nothing left to compare against by
 * the time a later step returns to the original clip. Restoring a play across a multi-step
 * detour would need to remember it past that clearing, which nothing here does.
 * Any other case falls through to clearing the play and leaving the anchor unresolved. Carrying a
 * play forward onto a clip it hasn't been confirmed to appear in would pair a UID with the wrong
 * clip in the outbound package — silently, since nothing on screen would show it.
 */
export function applyClipSubstitution(
  draft: AnchorDraftState,
  newClipAssetId: string,
  committedPlayVisibleInNewClip: boolean
): AnchorDraftState {
  const keepsPlay =
    draft.resolution &&
    (committedPlayVisibleInNewClip ||
      draft.resolution.clipAssetId === newClipAssetId);
  if (keepsPlay) {
    return {
      resolution: { ...draft.resolution!, clipAssetId: newClipAssetId },
      substitutedClipAssetId: newClipAssetId,
    };
  }
  return { resolution: null, substitutedClipAssetId: newClipAssetId };
}

/**
 * "Use Spotter's original clip" (`AnchorStage.tsx`'s `revertToOriginal`) — the one explicit action
 * left once stepping itself commits: restores the anchor's own suggested clip. Never resurrects a
 * play a prior substitution cleared: if `applyClipSubstitution` already dropped `resolution`,
 * there is nothing here to bring back — the anchor stays exactly as unresolved as it was, now on
 * its original clip, waiting for a fresh pick.
 */
export function undoClipSubstitution(
  draft: AnchorDraftState
): AnchorDraftState {
  return { resolution: draft.resolution, substitutedClipAssetId: null };
}

/**
 * A gamebook-row click or manual-UID entry (`AnchorConfirmContent.tsx`'s `handleCommit`) —
 * `clipAssetId` is always the anchor's CURRENT clip (`effectiveClipAssetId` below), which is
 * already up to date by the time a play can be clicked: stepping the reel commits a substitution
 * immediately, there's no separate "just previewing" window in between (see
 * `applyClipSubstitution`'s doc comment). Always replaces any pending clip-only substitution — the
 * resolution itself is now the single source of truth for this anchor's clip.
 */
export function commitPlay(
  uid: string,
  source: AnchorResolvedSource,
  clipAssetId: string
): AnchorDraftState {
  return {
    resolution: { uid, clipAssetId, source },
    substitutedClipAssetId: null,
  };
}

/**
 * The clip this anchor is currently committed to. A committed resolution wins (it's the final
 * decision); a pending substitution otherwise; the anchor's own suggestion if neither is set.
 * Feeds both `AnchorStage.tsx`'s badge/preview state and the outbound submit payload — reading
 * both off the same function is what keeps them from disagreeing.
 */
export function effectiveClipAssetId(
  draft: AnchorDraftState,
  suggestedClipAssetId: string
): string {
  return (
    draft.resolution?.clipAssetId ??
    draft.substitutedClipAssetId ??
    suggestedClipAssetId
  );
}
